import { useCallback, useEffect, useRef, useState } from "react";

import { ArrowLeft, FileText, Pencil, Plus, Trash2 } from "lucide-react";

import { logger } from "../../lib/logger.js";
import { useStorage } from "../../lib/storage/storage-context.js";
import type { DraftMeta } from "../../lib/storage/index.js";
import { useDraftsStore } from "../../stores/drafts-store.js";

import { useNewDraft } from "./useNewDraft.js";

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMs = now - then;
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return "Just now";
  if (min < 60) return `${String(min)} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${String(hr)} h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${String(day)} d ago`;
  return new Date(iso).toLocaleDateString();
}

export function DraftsScreen() {
  const { drafts, cloudAvailable } = useStorage();
  const list = useDraftsStore((s) => s.list);
  const loading = useDraftsStore((s) => s.loadingList);
  const setList = useDraftsStore((s) => s.setList);
  const setLoading = useDraftsStore((s) => s.setLoadingList);
  const openDraft = useDraftsStore((s) => s.openDraft);
  const removeMeta = useDraftsStore((s) => s.removeMeta);
  const upsertMeta = useDraftsStore((s) => s.upsertMeta);
  const setScreen = useDraftsStore((s) => s.setScreen);
  const newDraft = useNewDraft();
  const [pendingDelete, setPendingDelete] = useState<DraftMeta | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);

  const rename = useCallback(
    async (id: string, title: string) => {
      try {
        const meta = await drafts.rename(id, title);
        upsertMeta(meta);
      } catch (err) {
        logger.error("drafts.rename failed", err);
      } finally {
        setRenamingId(null);
      }
    },
    [drafts, upsertMeta],
  );

  const refresh = useCallback(async () => {
    if (!cloudAvailable) {
      setList([]);
      return;
    }
    setLoading(true);
    try {
      setList(await drafts.list());
    } catch (err) {
      logger.error("drafts.list failed", err);
    } finally {
      setLoading(false);
    }
  }, [cloudAvailable, drafts, setList, setLoading]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const open = useCallback(
    async (id: string) => {
      try {
        const rec = await drafts.load(id);
        if (!rec) return;
        openDraft(id, rec.state, rec.media ?? null);
      } catch (err) {
        logger.error("drafts.load failed", err);
      }
    },
    [drafts, openDraft],
  );

  const confirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    try {
      await drafts.delete(pendingDelete.id);
      removeMeta(pendingDelete.id);
    } catch (err) {
      logger.error("drafts.delete failed", err);
    } finally {
      setPendingDelete(null);
    }
  }, [drafts, pendingDelete, removeMeta]);

  return (
    <div className="flex h-full flex-col bg-[var(--tg-bg)]">
      <header className="flex items-center justify-between border-b border-[var(--tg-secondary-bg)] px-2 py-2">
        <button
          type="button"
          aria-label="Back to editor"
          className="inline-flex h-9 w-9 items-center justify-center rounded-md hover:bg-[var(--tg-secondary-bg)]"
          onClick={() => {
            setScreen("editor");
          }}
        >
          <ArrowLeft size={18} />
        </button>
        <h2 className="text-sm font-semibold">My drafts</h2>
        <button
          type="button"
          aria-label="New draft"
          className="inline-flex h-9 items-center gap-1 rounded-md bg-[var(--tg-link)] px-3 text-sm font-medium text-[var(--tg-button-text)]"
          onClick={() => {
            void newDraft();
          }}
        >
          <Plus size={16} /> New
        </button>
      </header>

      {!cloudAvailable && (
        <p
          role="alert"
          className="border-b border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
        >
          Telegram CloudStorage is not available in this client. Drafts will
          not sync across devices.
        </p>
      )}

      {loading && (
        <p className="p-3 text-sm text-[var(--tg-hint)]">Loading…</p>
      )}

      {!loading && list.length === 0 && (
        <div className="flex flex-1 flex-col items-center justify-center p-6 text-center text-[var(--tg-hint)]">
          <FileText size={36} className="mb-2 opacity-50" />
          <p className="text-sm">No drafts yet.</p>
          <button
            type="button"
            className="mt-3 text-sm text-[var(--tg-link)] underline-offset-2 hover:underline"
            onClick={() => {
            void newDraft();
          }}
          >
            Start a new draft
          </button>
        </div>
      )}

      <ul className="divide-y divide-[var(--tg-secondary-bg)] overflow-auto">
        {list.map((d) => (
          <DraftRow
            key={d.id}
            draft={d}
            renaming={renamingId === d.id}
            onOpen={() => {
              void open(d.id);
            }}
            onStartRename={() => {
              setRenamingId(d.id);
            }}
            onCancelRename={() => {
              setRenamingId(null);
            }}
            onSubmitRename={(title) => {
              if (title === d.title || title.trim().length === 0) {
                setRenamingId(null);
                return;
              }
              void rename(d.id, title);
            }}
            onDelete={() => {
              setPendingDelete(d);
            }}
          />
        ))}
      </ul>

      {pendingDelete && (
        <DeleteConfirmDialog
          title={pendingDelete.title}
          onCancel={() => {
            setPendingDelete(null);
          }}
          onConfirm={() => {
            void confirmDelete();
          }}
        />
      )}
    </div>
  );
}

function DraftRow(props: {
  draft: DraftMeta;
  renaming: boolean;
  onOpen: () => void;
  onStartRename: () => void;
  onCancelRename: () => void;
  onSubmitRename: (title: string) => void;
  onDelete: () => void;
}) {
  const { draft, renaming } = props;
  const [value, setValue] = useState(draft.title);
  const submittedRef = useRef(false);

  useEffect(() => {
    if (renaming) {
      setValue(draft.title);
      submittedRef.current = false;
    }
  }, [renaming, draft.title]);

  // Guard against Enter→submit then onBlur firing the second submit
  // when the input unmounts.
  const submit = (next: string): void => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    props.onSubmitRename(next);
  };

  return (
    <li className="flex items-start gap-2 px-3 py-3">
      <div className="min-w-0 flex-1">
        {renaming ? (
          <input
            autoFocus
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
            }}
            onFocus={(e) => {
              e.target.select();
            }}
            onBlur={() => {
              submit(value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submit(value);
              } else if (e.key === "Escape") {
                e.preventDefault();
                submittedRef.current = true;
                props.onCancelRename();
              }
            }}
            className="w-full rounded-md border border-[var(--tg-link)] bg-[var(--tg-bg)] px-2 py-1 text-sm text-[var(--tg-text)] outline-none"
            maxLength={60}
          />
        ) : (
          <button
            type="button"
            className="w-full text-left"
            onClick={props.onOpen}
          >
            <div className="flex items-baseline justify-between gap-3">
              <span className="truncate text-sm font-medium text-[var(--tg-text)]">
                {draft.title}
              </span>
              <span className="shrink-0 text-xs text-[var(--tg-hint)]">
                {formatRelative(draft.updated_at)}
              </span>
            </div>
            {draft.preview && (
              <p className="mt-1 line-clamp-1 text-xs text-[var(--tg-hint)]">
                {draft.preview}
              </p>
            )}
          </button>
        )}
      </div>
      {!renaming && (
        <>
          <button
            type="button"
            aria-label={`Rename ${draft.title}`}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-[var(--tg-hint)] hover:bg-[var(--tg-secondary-bg)]"
            onClick={props.onStartRename}
          >
            <Pencil size={16} />
          </button>
          <button
            type="button"
            aria-label={`Delete ${draft.title}`}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-[var(--tg-hint)] hover:bg-[var(--tg-secondary-bg)]"
            onClick={props.onDelete}
          >
            <Trash2 size={16} />
          </button>
        </>
      )}
    </li>
  );
}

function DeleteConfirmDialog(props: {
  title: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Delete draft"
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40"
      onClick={props.onCancel}
    >
      <div
        className="m-3 w-full max-w-md rounded-2xl bg-[var(--tg-bg)] p-4 shadow-2xl"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <h3 className="text-base font-semibold">Delete this draft?</h3>
        <p className="mt-1 text-sm text-[var(--tg-hint)]">
          “{props.title}” will be removed from Telegram CloudStorage.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            className="rounded-md px-3 py-2 text-sm hover:bg-[var(--tg-secondary-bg)]"
            onClick={props.onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded-md bg-red-600 px-3 py-2 text-sm font-medium text-white"
            onClick={props.onConfirm}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}
